import { useMemo, useState } from "react";
import {
  Choices, Details, Disclaimer, Nav, Panel, Progress, Question,
  ResultBlock, ResultFooter, ResultHeadline, ResultList, ToolFrame, useTool, useToolEvents, WorkableOptions,
} from "@/components/site/ToolShell";

const SLUG = "generator-coverage-estimator";

// Approximate running demand in kW per load, carried over from the existing tool. Square footage is
// deliberately absent: sizing is driven by the loads the homeowner wants to keep running.
const LOADS = [
  { id: "heating", label: "Heating system", w: 8, surge: true, group: "Protective" },
  { id: "fridge", label: "Refrigerator", w: 3, surge: true, group: "Protective" },
  { id: "freezer", label: "Freezer", w: 3, surge: true, group: "Protective" },
  { id: "well", label: "Well pump", w: 8, surge: true, group: "Protective" },
  { id: "sump", label: "Sump pump", w: 6, surge: true, group: "Protective" },
  { id: "medical", label: "Medical equipment", w: 2, group: "Protective" },
  { id: "lights", label: "Lights", w: 2, group: "Liveability" },
  { id: "outlets", label: "General outlets", w: 3, group: "Liveability" },
  { id: "internet", label: "Internet & networking", w: 1, group: "Liveability" },
  { id: "office", label: "Home office", w: 2, group: "Liveability" },
  { id: "tv", label: "TVs", w: 2, group: "Liveability" },
  { id: "theater", label: "Projector / home theater", w: 2, group: "Liveability" },
  { id: "microwave", label: "Microwave", w: 4, group: "Liveability" },
  { id: "washer", label: "Washer", w: 4, surge: true, group: "Convenience" },
  { id: "dryer", label: "Electric dryer", w: 12, group: "Capacity-driving" },
  { id: "range", label: "Electric range", w: 14, group: "Capacity-driving" },
  { id: "water_heater", label: "Electric water heater", w: 12, group: "Capacity-driving" },
  { id: "ac", label: "Central air conditioning", w: 16, surge: true, group: "Capacity-driving" },
];

const STATES = ["MUST HAVE", "WOULD LIKE", "DON'T NEED"];
const GROUPS = ["Protective", "Liveability", "Convenience", "Capacity-driving"];

function evaluate(f) {
  const s = f.states || {};
  const pick = (fn) => LOADS.filter((l) => fn(s[l.id] || null));
  const must = pick((v) => v === "MUST HAVE");
  const wanted = pick((v) => v === "MUST HAVE" || v === "WOULD LIKE");

  const sum = (arr) => arr.reduce((a, l) => a + l.w, 0);
  const manage = f.loadManagement === "Yes — load management is acceptable";

  // Low end: must-have loads. With load management, the largest capacity-driving loads are
  // sequenced rather than carried simultaneously, so only the largest of them counts.
  const mustBig = must.filter((l) => l.group === "Capacity-driving");
  const mustSmall = must.filter((l) => l.group !== "Capacity-driving");
  const low = manage && mustBig.length > 1
    ? sum(mustSmall) + Math.max(...mustBig.map((l) => l.w))
    : sum(must);

  // High end: everything wanted, running together, no sequencing.
  const high = sum(wanted);

  const surgeLoads = wanted.filter((l) => l.surge);
  const largestMotor = surgeLoads.length ? surgeLoads.slice().sort((a, b) => b.w - a.w)[0] : null;
  const drivers = wanted.slice().sort((a, b) => b.w - a.w).filter((l) => l.w >= 6).slice(0, 5);
  const mustDrivers = must.slice().sort((a, b) => b.w - a.w).slice(0, 4);

  const lowR = Math.max(0, Math.round(low));
  const highR = Math.max(lowR, Math.round(high));
  const hasRange = must.length > 0;

  const notes = [];
  if (largestMotor)
    notes.push(`Motor and compressor loads you selected (${surgeLoads.map((l) => l.label).join(", ")}) draw substantially more on startup than while running. The starting demand of the largest of these — ${largestMotor.label} — must be added on top of this running range by whoever sizes the unit. These figures do not assume every motor starts at the same moment.`);
  else
    notes.push("You have not selected motor or compressor loads, so starting surge is less likely to drive the sizing. Confirm against the nameplate data of anything you add later.");
  if (manage)
    notes.push("With load management accepted, large loads can be sequenced instead of running together, which lets a smaller unit serve more circuits.");
  else
    notes.push("Without load management, the unit must carry your selected loads running together, which pushes the requirement toward the top of the range.");

  const fuel = [];
  if (f.fuel === "Natural gas") fuel.push("Natural gas removes refuelling, but the supply must be adequately sized for the unit — confirm with your installer and gas utility.");
  if (f.fuel === "Propane") fuel.push("Propane requires tank capacity sized to your expected outage duration. Longer outages mean a larger tank, not just a larger generator.");
  if (f.fuel === "Diesel") fuel.push("Diesel requires on-site storage, fuel management and periodic exercise.");
  if (f.fuel === "Not sure") fuel.push("Fuel availability at your property usually decides this before capacity does. Confirm what is available before choosing a unit.");

  const install = [
    "Final sizing depends on a load calculation performed against your actual panel and appliance nameplate data.",
    "Transfer switch type, and whether you use a whole-panel or essential-loads configuration, materially affects both cost and coverage.",
    "Placement, clearances, noise and local permitting all need to be confirmed before installation.",
  ];

  // Directions derived from the range, fuel and load-management answers already calculated.
  const workable = [];
  if (hasRange) {
    workable.push(`Standby or portable units whose published running capacity covers ${lowR}–${highR} kW, before the largest motor's starting demand is added`);
    workable.push(
      manage
        ? "A load-management setup that sequences your large loads instead of running them together"
        : "Capacity toward the top of this range, since you want your selected loads running together"
    );
    workable.push(
      mustBig.length
        ? "An essential-loads or whole-panel transfer switch, since capacity-driving loads are on your must-have list"
        : "An essential-loads transfer switch covering the circuits you marked must-have"
    );
    if (f.fuel !== "Not sure") workable.push(`${f.fuel}-fuelled units, subject to supply or tank sizing at your property`);
    else workable.push("Fuel type stays open until you confirm what is available at the property");
  } else {
    workable.push("Mark at least one must-have load and we can show the capacity range that fits");
  }

  return { lowR, highR, hasRange, mustDrivers, drivers, notes, fuel, install, must, wanted, workable,
    optionsHeadline: hasRange ? "Backup power configurations that fit this range" : "Tell us what must stay running" };
}

const INITIAL = {
  states: { heating: "MUST HAVE", fridge: "MUST HAVE", freezer: "MUST HAVE", lights: "MUST HAVE", outlets: "MUST HAVE", internet: "MUST HAVE" },
  fuel: "Natural gas",
  loadManagement: "Yes — load management is acceptable",
};

export default function GeneratorEstimator() {
  const tool = useTool(SLUG);
  const { start, complete, resetRun } = useToolEvents(SLUG);
  const [step, setStep] = useState(0);
  const [f, setF] = useState(INITIAL);
  const [result, setResult] = useState(null);
  const total = 2;

  const setState = (id, v) => { start(); setF((s) => ({ ...s, states: { ...s.states, [id]: v } })); };
  const set = (k) => (v) => { start(); setF((s) => ({ ...s, [k]: v })); };

  const mustCount = useMemo(
    () => LOADS.filter((l) => f.states[l.id] === "MUST HAVE").length,
    [f.states]
  );

  const next = () => {
    start();
    if (step < total - 1) return setStep(step + 1);
    const r = evaluate(f);
    setResult(r);
    complete({ low: r.lowR, high: r.highR });
  };

  if (!tool) return <div className="h-[60vh]" />;

  return (
    <ToolFrame tool={tool}>
      <Panel>
        {!result ? (
          <>
            <Progress step={step} total={total} />

            {step === 0 && (
              <Question
                label="What do you actually want to keep running?"
                hint="Sizing starts from your loads, not from the size of your house. Mark each one."
              >
                <div className="sticky top-[76px] md:top-[88px] lg:top-[96px] xl:top-[104px] z-10 bg-white/95 backdrop-blur border-b border-line py-3 mb-4 flex items-center justify-between">
                  <span className="text-[11px] tracking-[0.16em] uppercase text-[#6E6A63]">Must-have loads selected</span>
                  <span className="display text-[19px] text-charcoal" data-testid="live-must-count">{mustCount}</span>
                </div>
                {GROUPS.map((g) => (
                  <div key={g} className="mb-7">
                    <div className="text-[11.5px] tracking-[0.16em] uppercase text-[#48563F] font-semibold mb-3">{g}</div>
                    <div className="space-y-2">
                      {LOADS.filter((l) => l.group === g).map((l) => (
                        <div key={l.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 border border-line bg-white px-4 py-3">
                          <span className="text-[15px] text-charcoal flex-1">{l.label}</span>
                          <div className="flex gap-1.5">
                            {STATES.map((st) => (
                              <button
                                key={st}
                                type="button"
                                onClick={() => setState(l.id, st)}
                                data-testid={`load-${l.id}-${st.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                className={`text-[11.5px] tracking-[0.06em] uppercase font-medium px-3 py-2 border transition-colors ${
                                  f.states[l.id] === st ? "border-sage bg-sage-soft text-charcoal" : "border-line text-[#7A766F] hover:border-[#BFC7BA]"
                                }`}
                              >
                                {st}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </Question>
            )}

            {step === 1 && (
              <>
                <Question label="What fuel is available at the property?" hint="Fuel availability often decides the unit before capacity does.">
                  <Choices testId="choice-fuel" options={["Natural gas", "Propane", "Diesel", "Not sure"]} value={f.fuel} onChange={set("fuel")} />
                </Question>
                <Question
                  label="Is load management acceptable?"
                  hint="Load management sequences large loads instead of running them together, which changes the size of unit you need."
                >
                  <Choices
                    testId="choice-load-management"
                    cols={1}
                    options={[
                      { value: "Yes — load management is acceptable", label: "Yes — sequencing large loads is fine", note: "Lets a smaller unit serve more circuits" },
                      { value: "No — everything should run together", label: "No — everything should run together", note: "Requires more capacity" },
                    ]}
                    value={f.loadManagement}
                    onChange={set("loadManagement")}
                  />
                </Question>
              </>
            )}

            <Nav step={step} total={total} onBack={() => setStep(Math.max(0, step - 1))} onNext={next} disabled={step === 0 && mustCount === 0} />
          </>
        ) : (
          <div data-testid="tool-result">
            <ResultHeadline
              eyebrow="Estimated planning range"
              headline={result.hasRange ? `Estimated generator range: ${result.lowR}–${result.highR} kW` : "Select at least one must-have load"}
              body="This is an estimated planning range, not a specification. It is calculated from the loads you selected, not from the size of your home."
              reasons={result.mustDrivers.map((l) => `Must-have: ${l.label}`)}
              testId="result-headline"
            />
            <WorkableOptions
              tool={tool}
              headline={result.optionsHeadline}
              options={result.workable}
              productPath="Standby and portable generators in this capacity range, plus transfer switches"
            />
            <div className="border-t-2 border-charcoal mt-12">
              <ResultBlock
                label="Must-have loads driving the low end"
                value={result.must.map((l) => l.label).join(", ") || "None selected"}
                testId="result-must-loads"
              />
              <ResultList label="Surge and load-management note" items={result.notes} testId="result-surge" />
              {result.fuel.length > 0 && <ResultList label="Fuel context" items={result.fuel} testId="result-fuel" />}
              <Details label="See sizing and installation details" testId="result-details">
                <ResultList
                  label="Largest loads in your selection"
                  items={result.drivers.length ? result.drivers.map((l) => `${l.label} — approximately ${l.w} kW running`) : ["No individually large loads selected."]}
                  testId="result-drivers"
                />
                <ResultList label="Installation considerations" items={result.install} testId="result-install" />
              </Details>
            </div>

            <Disclaimer>
              Estimated planning range only. Actual equipment nameplate information, starting-surge
              demand and a professional load calculation determine final generator selection. This is
              not an electrical design or installation specification.
            </Disclaimer>

            <div className="mt-2">
              <ResultFooter onReset={() => { resetRun(); setResult(null); setStep(0); setF(INITIAL); }} />
            </div>
          </div>
        )}
      </Panel>
    </ToolFrame>
  );
}
